import "core-js/modules/es.string.replace.js";
import "core-js/modules/web.dom-collections.iterator.js";
import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { IconButton, Menu, styled } from '@mui/material';
import KeyboardArrowDownIcon from '@pixel/assets/icons/KeyboardArrowDown';
import MoreVert from '@pixel/assets/icons/MoreVert';
// import { SortDown } from '@pixel/assets';

// eslint-disable-next-line import/no-cycle
import Button from '../Button';
import PrimaryActionButton from './PrimaryActionButton';
import ActionMenu from './ActionMenu';
const OVERFLOW_BUTTON_WIDTH_PX = 0;
const ActionGroupContainer = styled('div')(() => ({
  display: 'flex',
  overflow: 'hidden',
  alignItems: 'center',
  minWidth: "".concat(OVERFLOW_BUTTON_WIDTH_PX, "px")
}));
const ActionGroupButtonText = styled('span')(() => ({
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden'
}));
function useActionButtons(id, actions) {
  const actionButtons = useMemo(() => {
    const primaryButtons = actions.filter(action => action.priority);
    return primaryButtons.map(action => ({
      ...action,
      id: "".concat(id, "_").concat(action.label)
    }));
  }, [actions]);
  const actionButtonMap = useMemo(() => actionButtons.reduce((acc, action) => {
    acc[action.id] = action;
    return acc;
  }, {}), [actionButtons]);
  const alwaysOverflowList = useMemo(() => actions.filter(action => !action.priority), [actions]);
  return useMemo(() => ({
    actionButtons,
    actionButtonMap,
    alwaysOverflowList
  }), [actions]);
}
export const ActionGroup = _ref => {
  let {
    id,
    actions,
    data,
    actionLabel = 'Actions',
    showSubActionDividers = false,
    disableIconButton = false,
    disabled = false,
    ariaLabelOverflow = 'Overflow menu for action ${id}',
    ariaLabelDropdown = 'dropdown',
    variant = "standard_more"
  } = _ref;
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const timeoutRef = useRef(null);
  const {
    actionButtons,
    actionButtonMap,
    alwaysOverflowList
  } = useActionButtons(id, actions);
  const [shownActions, setShownActions] = useState(actionButtons);
  const [hiddenActions, setHiddenActions] = useState([]);
  const [parentWidth, setParentWidth] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleOverflows = () => {
    const container = rootRef.current;
    if (container.offsetHeight < container.scrollHeight || container.offsetWidth < container.scrollWidth) {
      let runningTotal = OVERFLOW_BUTTON_WIDTH_PX;

      // Reset the runningTotal to the overflow button's width if it exists
      // this is so we leave room for the overflow button and everything else that overflows will be hidden
      // not sure why but * 2 seems to work perfectly
      if (btnRef.current && btnRef.current.offsetWidth) {
        runningTotal = btnRef.current.offsetWidth * 2;
      }
      const newShownActions = [];
      const newHiddenActions = [];
      const {
        children
      } = container;
      let pushRemaining = false;
      children.forEach(child => {
        const item = actionButtonMap[child.id];
        if (!item) {
          // runningTotal += child.offsetWidth;
          return;
        }
        if (child.offsetWidth + runningTotal <= container.offsetWidth && !pushRemaining) {
          runningTotal += child.offsetWidth;
          newShownActions.push(item);
        } else {
          newHiddenActions.push(item);
          pushRemaining = true;
        }
      });
      setShownActions(newShownActions);
      setHiddenActions(newHiddenActions);
    } else {
      setShownActions(actionButtons);
      setHiddenActions([]);
    }
  };
  useEffect(() => {
    setShownActions(actionButtons);
  }, [actionButtons]);
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(handleOverflows, 250);
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [parentWidth]);
  const handleTimeOutResize = () => {
    if (rootRef.current && rootRef.current.offsetWidth) {
      setParentWidth(rootRef.current.offsetWidth);
    }
  };
  const handleResize = () => {
    setTimeout(handleTimeOutResize, 800);
  };
  useLayoutEffect(() => {
    if (rootRef.current) {
      handleTimeOutResize();
      window.addEventListener('resize', handleResize);
      const observer = new ResizeObserver(handleResize);
      observer.observe(rootRef.current);
      return () => {
        window.removeEventListener('resize', handleResize);
        if (rootRef.current) {
          observer.unobserve(rootRef.current);
        }
        observer.disconnect();
      };
    }
    return () => {};
  }, []);
  const overflowActions = useMemo(() => [...hiddenActions, ...alwaysOverflowList], [alwaysOverflowList, hiddenActions]);
  const allActionsDisabled = disabled;
  return /*#__PURE__*/React.createElement(ActionGroupContainer, {
    ref: rootRef,
    id: id
  }, shownActions.map(action => /*#__PURE__*/React.createElement(PrimaryActionButton, {
    key: action.id,
    action: action,
    data: data,
    hidden: false,
    disabled: allActionsDisabled,
    ariaLabelDropdown: ariaLabelDropdown
  })), (hiddenActions.length > 0 || alwaysOverflowList.length > 0) && (variant === "standard_more" ? /*#__PURE__*/React.createElement(IconButton, {
    ref: btnRef,
    disabled: disableIconButton,
    "data-tertiary": true,
    onClick: handleClick,
    name: ariaLabelOverflow.replace("${id}", id),
    "aria-label": ariaLabelOverflow.replace("${id}", id)
  }, /*#__PURE__*/React.createElement(MoreVert, null)) : /*#__PURE__*/React.createElement(Button, {
    ref: btnRef,
    variant: "outlined",
    disableElevation: true,
    "data-tertiary": true,
    onClick: handleClick,
    name: ariaLabelOverflow.replace("${id}", id),
    "aria-label": ariaLabelOverflow.replace("${id}", id),
    hidden: false,
    endIcon: /*#__PURE__*/React.createElement(KeyboardArrowDownIcon, null),
    sx: {
      whiteSpace: 'nowrap',
      marginLeft: '5px',
      marginRight: '5px',
      minWidth: 'unset'
    }
  }, actionLabel)), hiddenActions.map(action => /*#__PURE__*/React.createElement(PrimaryActionButton, {
    key: action.id,
    action: action,
    disabled: allActionsDisabled,
    data: data,
    hidden: true,
    ariaLabelDropdown: ariaLabelDropdown
  })), /*#__PURE__*/React.createElement(Menu, {
    anchorEl: anchorEl,
    open: open,
    onClose: handleClose,
    "data-tertiary": true,
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'right'
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'right'
    }
  }, /*#__PURE__*/React.createElement(ActionMenu, {
    actions: overflowActions,
    disabled: allActionsDisabled,
    data: data,
    showSubActionDividers: showSubActionDividers,
    onCloseMenu: handleClose
  })));
};
export default ActionGroup;